import type { CSSProperties } from "react";

/**
 * "Calendar coordination — A clearer week, by design."
 * A calm week-grid visualization: BunnyCal layers everyone's commitments into one picture,
 * meetings landing where they fit rather than where they collide. Ported from the Claude Design
 * mockup; kept honest (illustrative calendar only — no fabricated metric cards).
 *
 * Motion is driven entirely by the shared [data-reveal]/[data-reveal-group] hooks in
 * useLandingMotion, so entries stagger in on scroll-down and reverse on scroll-up automatically.
 */

const DAYS = ["Mon 12", "Tue 13", "Wed 14", "Thu 15", "Fri 16"];

// One row per hour, 9am → 5pm. 66px per hour matches the event top/height math below.
const HOURS = ["9am", "10am", "11am", "12pm", "1pm", "2pm", "3pm", "4pm", "5pm"];

const LEGEND: Array<{ label: string; color: string }> = [
  { label: "Meetings", color: "var(--lp-lilac)" },
  { label: "Focus", color: "var(--lp-sage)" },
  { label: "1:1s", color: "var(--lp-peach)" },
  { label: "External", color: "var(--lp-blush)" },
  { label: "Buffer", color: "var(--lp-butter)" },
];

type Ev = { top: number; height: number; kicker: string; title: string; soft: string; border: string };

// top/height in px on a 528px rail (66px/hour). Colours map to the legend categories.
const WEEK: Ev[][] = [
  // Mon
  [
    { top: 0,   height: 60,  kicker: "Team · 9:00",       title: "Standup",    soft: "var(--lp-lilac-soft)",  border: "var(--lp-lilac)" },
    { top: 132, height: 126, kicker: "Mira + Jules · 11:00", title: "Design crit", soft: "var(--lp-peach-soft)", border: "var(--lp-peach)" },
    { top: 396, height: 60,  kicker: "Focus · 3:00",       title: "Quiet hour", soft: "var(--lp-butter-soft)", border: "var(--lp-butter)" },
  ],
  // Tue
  [
    { top: 0,   height: 126, kicker: "Focus · 9–11",      title: "Maker time", soft: "var(--lp-sage-soft)",  border: "var(--lp-sage)" },
    { top: 231, height: 60,  kicker: "With Jordan · 12:30", title: "Intro chat", soft: "var(--lp-blush-soft)", border: "var(--lp-blush)" },
    { top: 396, height: 60,  kicker: "Northwind · 3:00",  title: "Review",     soft: "var(--lp-lilac-soft)", border: "var(--lp-lilac)" },
  ],
  // Wed
  [
    { top: 33,  height: 60,  kicker: "Kira · 9:30",       title: "1:1 with K.", soft: "var(--lp-peach-soft)", border: "var(--lp-peach)" },
    { top: 99,  height: 126, kicker: "Studio · 10:30",    title: "Workshop",   soft: "var(--lp-lilac-soft)", border: "var(--lp-lilac)" },
    { top: 363, height: 60,  kicker: "With Leo · 2:30",   title: "Coffee",     soft: "var(--lp-butter-soft)", border: "var(--lp-butter)" },
  ],
  // Thu
  [
    { top: 0,   height: 258, kicker: "No meetings · 9–1", title: "Maker time", soft: "var(--lp-sage-soft)",  border: "var(--lp-sage)" },
    { top: 297, height: 60,  kicker: "With Foundry · 1:30", title: "Demo",      soft: "var(--lp-blush-soft)", border: "var(--lp-blush)" },
  ],
  // Fri
  [
    { top: 0,   height: 60,  kicker: "Team · 9:00",       title: "Standup",    soft: "var(--lp-lilac-soft)", border: "var(--lp-lilac)" },
    { top: 99,  height: 126, kicker: "Focus · 10:30",     title: "Walk + think", soft: "var(--lp-sage-soft)", border: "var(--lp-sage)" },
    { top: 363, height: 60,  kicker: "Team · 2:30",       title: "Wrap-up",    soft: "var(--lp-butter-soft)", border: "var(--lp-butter)" },
  ],
];

const dayHeadStyle: CSSProperties = {
  textAlign: "center",
  fontFamily: "var(--lp-mono)",
  fontSize: 11,
  letterSpacing: ".08em",
  color: "var(--lp-plum-300)",
  paddingBottom: 12,
  borderBottom: "1px solid var(--lp-border)",
  borderLeft: "1px solid var(--lp-border)",
};

const railLabelStyle: CSSProperties = {
  position: "absolute",
  right: 9,
  transform: "translateY(-6px)",
  fontFamily: "var(--lp-mono)",
  fontSize: 10.5,
  color: "var(--lp-plum-300)",
};

const eventKicker: CSSProperties = {
  fontFamily: "var(--lp-mono)",
  fontSize: 9.5,
  letterSpacing: ".04em",
  textTransform: "uppercase",
  color: "var(--lp-plum-400)",
};

export function LandingCoordination() {
  return (
    <section id="coordination" style={{ padding: "var(--lp-sec-y) 0", background: "var(--lp-ivory-2)" }}>
      <div className="lp-container">
        {/* The leading rule is drawn by .lp-eyebrow::before, as on every other section. This
            markup used to add a second inline line on top of it, which rendered as a double
            dash before the label. */}
        <span className="lp-eyebrow" data-reveal style={{ color: "var(--lp-plum-400)" }}>
          Calendar coordination
        </span>

        <h2 className="lp-h1" data-reveal style={{ marginTop: 12, maxWidth: "16ch" }}>
          A clearer week, <em>by design.</em>
        </h2>

        <p className="lp-lede" data-reveal style={{ marginTop: 18, maxWidth: "46ch" }}>
          BunnyCal layers everyone's commitments into one calm picture. Meetings land where
          they fit, never where they collide.
        </p>

        {/* Week panel — 3D tilt driven by scroll + pointer (see useLandingMotion [data-tilt]).
            The scene owns the perspective; the inner panel is what actually rotates. */}
        <div
          data-tilt-scene
          data-reveal
          style={{
            marginTop: 38,
            perspective: 1400,
            perspectiveOrigin: "center",
          }}
        >
        <div
          data-tilt
          style={{
            position: "relative",
            overflow: "hidden",
            background: "var(--lp-cream)",
            border: "1px solid var(--lp-border)",
            borderRadius: "var(--lp-radius-lg)",
            boxShadow: "var(--lp-shadow-card)",
            transformStyle: "preserve-3d",
            transformOrigin: "center",
            willChange: "transform",
          }}
        >
          {/* soft corner wash */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: "0 40% 55% 0",
              background: "linear-gradient(135deg, var(--lp-lilac-soft) 0%, transparent 65%)",
              opacity: 0.6,
              pointerEvents: "none",
            }}
          />

          <div className="lp-week-panel-body" style={{ position: "relative", zIndex: 1, padding: "clamp(22px,3vw,36px)" }}>
            {/* panel header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 20, flexWrap: "wrap" }}>
              <div>
                <span style={{ fontFamily: "var(--lp-mono)", fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--lp-plum-400)" }}>
                  This week · May 12 – 16
                </span>
                <div style={{ fontFamily: "var(--lp-serif)", fontWeight: 500, fontSize: "clamp(24px,2.6vw,34px)", color: "var(--lp-plum-900)", marginTop: 8 }}>
                  Studio · Pacific time
                </div>
              </div>
              <div className="lp-week-legend" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {LEGEND.map((l) => (
                  <span
                    key={l.label}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      fontFamily: "var(--lp-mono)",
                      fontSize: 10.5,
                      letterSpacing: ".06em",
                      textTransform: "uppercase",
                      color: "var(--lp-plum-500)",
                      padding: "6px 12px",
                      borderRadius: "var(--lp-radius-pill)",
                      border: "1px solid var(--lp-border)",
                      background: "var(--lp-cream)",
                    }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: l.color }} />
                    {l.label}
                  </span>
                ))}
              </div>
            </div>

            {/* week grid: 48px time rail + 5 day columns.
                `lp-week` is the hook the mobile rules key off — below md the
                grid drops to three days so the remaining columns are wide
                enough to hold an event title on one line. See landing.css. */}
            <div
              className="lp-week"
              data-reveal-group
              style={{
                display: "grid",
                gridTemplateColumns: "48px repeat(5, 1fr)",
                marginTop: 26,
              }}
            >
              {/* header row */}
              <div style={{ borderBottom: "1px solid var(--lp-border)" }} />
              {DAYS.map((d, i) => (
                <div key={d} className={`lp-week-dayhead lp-week-d${i}`} style={dayHeadStyle}>{d}</div>
              ))}

              {/* time rail */}
              <div className="lp-week-rail" style={{ position: "relative", height: "calc(528 * var(--lp-week-scale, 1px))" }}>
                {HOURS.map((h, i) => (
                  <span key={h} className="lp-week-hour" style={{ ...railLabelStyle, top: `calc(${i * 66} * var(--lp-week-scale, 1px))` }}>{h}</span>
                ))}
              </div>

              {/* day columns */}
              {WEEK.map((events, di) => (
                <div key={DAYS[di]} className={`lp-week-col lp-week-d${di}`} style={{ position: "relative", height: "calc(528 * var(--lp-week-scale, 1px))", borderLeft: "1px solid var(--lp-border)" }}>
                  {events.map((ev, ei) => (
                    <div
                      key={ei}
                      className="lp-week-ev"
                      data-reveal
                      style={{
                        position: "absolute",
                        left: 5,
                        right: 5,
                        // The rail is 66px/hour on desktop. Mobile scales that
                        // down via --lp-week-scale (see landing.css), so these
                        // pixel values are expressed as multiples of it rather
                        // than being overridden one by one.
                        top: `calc(${ev.top} * var(--lp-week-scale, 1px))`,
                        height: `calc(${ev.height} * var(--lp-week-scale, 1px))`,
                        borderRadius: 12,
                        padding: "9px 11px",
                        border: `1px solid ${ev.border}`,
                        background: ev.soft,
                        overflow: "hidden",
                      }}
                    >
                      <div className="lp-week-ev-kicker" style={eventKicker}>{ev.kicker}</div>
                      <div className="lp-week-ev-title" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--lp-plum-900)", marginTop: 3 }}>
                        {ev.title}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
        </div>
      </div>
    </section>
  );
}
